import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import useParamHook from '@Hooks/useParamHook';
import { useInView } from 'react-intersection-observer';
import { Button, CircularProgress, Drawer, Skeleton  } from "@mui/material";
import {
    ArrowLeft, Sparkles, TrendingUp, Clock, PhoneCall,
    PauseCircle, CheckCircle, XCircle, Ban,
    Pencil, X, Save, CheckCircle2,
    Send, AlertTriangle, RefreshCw, Tag, User, Trash2
} from "lucide-react";
import SnackbarManager from '@CommonFunctionalDisplay/GlobalSnackbar/SnackbarManager';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import { useReduxLeads } from '@Hooks/reduxLeadsHook/useReduxLeads';
import DeleteNameCardForm from '@RegistriesForms/DeleteNameCardForm';
import LeadsService from '@Helpers/URLHelpers/LeadsService';
import LabelClickUpMenu from '@CommonRepresentationalDisplay/LabelClickUpMenu';
import CreateLeadsForm from '@FormForms/CreateLeadsForm';
import UserUnsubscribe from '@CommonFunctionalDisplay/UserUnsubscribe';
import NameCardService from '@Helpers/URLHelpers/WeboutService/NameCardService';
import RegistryLinkViewService from '@Helpers/URLHelpers/WeboutService/Views/RegistryLinkViewService';
import CampaignJobService from '@Helpers/URLHelpers/CampaignJobService';
import ErrorHandler from '@Root/ErrorHandler';
import "./ContactDetailsPage.scss";

const safeJsonParse = (value, fallback = null) => {
    try { return typeof value === 'string' ? JSON.parse(value) : value; }
    catch { return fallback; }
};

const fmt = (ts) => {
    if (!ts) return '—';
    try { return new Date(ts).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }); }
    catch { return ts; }
};

// ─── Status maps ──────────────────────────────────────────────────────────────

const LEAD_STATUS_MAP = {
    newLeads:  { className: 'status-new',         icon: <Sparkles size={11} />,    label: 'New Lead'  },
    contacted: { className: 'status-hot',         icon: <TrendingUp size={11} />,  label: 'Contacted' },
    active:    { className: 'status-warm',        icon: <Clock size={11} />,       label: 'Active'    },
    followUp:  { className: 'status-info',        icon: <PhoneCall size={11} />,   label: 'Follow Up' },
    onHold:    { className: 'status-muted',       icon: <PauseCircle size={11} />, label: 'On Hold'   },
    onboarded: { className: 'status-success',     icon: <CheckCircle size={11} />, label: 'Onboarded' },
    closed:    { className: 'status-danger',      icon: <XCircle size={11} />,     label: 'Closed'    },
    lost:      { className: 'status-danger-soft', icon: <Ban size={11} />,         label: 'Lost'      },
};
const getLeadStatus = (s) => LEAD_STATUS_MAP[s] ?? { className: 'status-default', icon: null, label: s };

const CAMPAIGN_STATUS_MAP = {
    pending:    { className: 'cj-pending',   icon: <Clock size={12} />,         label: 'Pending'    },
    queued:     { className: 'cj-pending',   icon: <Clock size={12} />,         label: 'Queued'     },
    processing: { className: 'cj-progress',  icon: <RefreshCw size={12} />,     label: 'Processing' },
    sent:       { className: 'cj-sent',      icon: <Send size={12} />,          label: 'Sent'       },
    delivered:  { className: 'cj-delivered', icon: <CheckCircle2 size={12} />,  label: 'Delivered'  },
    read:       { className: 'cj-read',      icon: <CheckCircle2 size={12} />,  label: 'Read'       },
    failed:     { className: 'cj-failed',    icon: <AlertTriangle size={12} />, label: 'Failed'     },
};
const getCampaignStatus = (s) => CAMPAIGN_STATUS_MAP[s] ?? { className: 'cj-default', icon: null, label: s ?? '—' };

// ─── Editable fields config ───────────────────────────────────────────────────

const CONTACT_FIELDS = [
    { key: 'name',          label: 'Full Name',   type: 'text',     required: true },
    { key: 'designation',   label: 'Designation', type: 'text'                     },
    { key: 'department',    label: 'Department',  type: 'text'                     },
    { key: 'email',         label: 'Email',       type: 'email'                    },
    { key: 'areaCode',      label: 'Area Code',   type: 'text'                     },
    { key: 'contactNumber', label: 'Phone',       type: 'tel',      required: true },
    { key: 'organizationName',label: 'Organization',type: 'text'                 },
    { key: 'state',         label: 'State',       type: 'text'                     },
    { key: 'country',       label: 'Country',     type: 'text'                     },
    { key: 'pinCode',       label: 'Pin Code',    type: 'text'                     },
    { key: 'remarks',       label: 'Remarks',     type: 'textarea'                 },
];

// ─── Small reusable pieces ────────────────────────────────────────────────────

const Item = ({ label, value }) => (
    <div className="item">
        <div className="label">{label}</div>
        <div className="value">{value || "—"}</div>
    </div>
);

const LeadRow = ({ lead }) => {
    const status = getLeadStatus(lead.status);
    return (
        <tr>
            <td><span className="dot" /></td>
            <td>
                <div className="lead-name">{lead.name || "—"}</div>
                <div className="lead-email">{lead.email}</div>
            </td>
            <td>{lead.contactNumber || "—"}</td>
            <td>
                <span className={`lead-status ${status.className}`}>
                    {status.icon}{status.label}
                </span>
            </td>
            <td className="truncate">{lead.query || "—"}</td>
        </tr>
    );
};

// ─── Edit form ────────────────────────────────────────────────────────────────

const ContactEditForm = ({ contact, onSave, onCancel, saving }) => {
    const [form, setForm] = useState(() => {
        const init = {};
        CONTACT_FIELDS.forEach(f => { init[f.key] = contact?.[f.key] ?? ''; });
        return init;
    });
    const [errors, setErrors] = useState({});

    const validate = () => {
        const errs = {};
        CONTACT_FIELDS.forEach(f => {
            if (f.required && !String(form[f.key] ?? '').trim()) errs[f.key] = `${f.label} is required`;
        });
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleChange = (key, val) => {
        setForm(p => ({ ...p, [key]: val }));
        if (errors[key]) setErrors(p => ({ ...p, [key]: undefined }));
    };

    return (
        <div className="edit-form">
            <div className="edit-form__grid">
                {CONTACT_FIELDS.map(field => (
                    <div
                        key={field.key}
                        className={`edit-form__field ${field.key === 'remarks' ? 'edit-form__field--full' : ''} ${errors[field.key] ? 'edit-form__field--error' : ''}`}
                    >
                        <label className="edit-form__label">
                            {field.label}
                            {field.required && <span className="edit-form__required">*</span>}
                        </label>
                        {field.type === 'textarea' ? (
                            <textarea
                                className="edit-form__input edit-form__textarea"
                                value={form[field.key]}
                                onChange={e => handleChange(field.key, e.target.value)}
                                rows={3}
                                placeholder={`Enter ${field.label.toLowerCase()}`}
                            />
                        ) : (
                            <input
                                className="edit-form__input"
                                type={field.type}
                                value={form[field.key]}
                                onChange={e => handleChange(field.key, e.target.value)}
                                placeholder={`Enter ${field.label.toLowerCase()}`}
                            />
                        )}
                        {errors[field.key] && <span className="edit-form__error-msg">{errors[field.key]}</span>}
                    </div>
                ))}
            </div>
            <div className="edit-form__actions">
                <button className="edit-form__btn edit-form__btn--cancel" onClick={onCancel} disabled={saving}>
                    <X size={14} /> Cancel
                </button>
                <button className="edit-form__btn edit-form__btn--save" onClick={() => { if (validate()) onSave(form); }} disabled={saving}>
                    {saving
                        ? <><CircularProgress size={13} sx={{ color: '#fff' }} /> Saving…</>
                        : <><Save size={14} /> Save Changes</>
                    }
                </button>
            </div>
        </div>
    );
};

// ─── Campaign Job section ─────────────────────────────────────────────────────

const CampaignJobSection = ({ campaignJob }) => {
    if (!campaignJob) return null;

    const status   = getCampaignStatus(campaignJob.status);
    const details  = campaignJob.details ?? {};

    // Flatten details into displayable key-value pairs, skip empty/object values
    const detailRows = Object.entries(details).filter(([, v]) => {
        if (v === null || v === undefined || v === '') return false;
        if (typeof v === 'object') return false;
        return true;
    });

    return (
        <div className="section">
            <div className="section-title section-title--with-action">
                <span>Campaign Job</span>
                <span className={`cj-status-badge ${status.className}`}>
                    {status.icon}{status.label}
                </span>
            </div>

            <div className="cj-body">
                {/* Core meta row */}
                     {
//                <div className="cj-meta">
//                    {campaignJob.createTimeStamp && (
//                        <div className="cj-meta__item">
//                            <span className="cj-meta__key">Created</span>
//                            <span className="cj-meta__val">{fmt(campaignJob.createTimeStamp)}</span>
//                        </div>
//                    )}
//                </div>
                     }

                {/* Error banner (failed only) */}
                {campaignJob.status === 'failed' && details.error && (
                    <div className="cj-error-banner">
                        <AlertTriangle size={14} />
                        <span>{details.error}</span>
                    </div>
                )}

                {/* Dynamic detail rows from parsed details object */
//                {detailRows.length > 0 && (
//                    <div className="cj-details-grid">
//                        {detailRows
//                            .filter(([k]) => k !== 'error') // error already shown above
//                            .map(([k, v]) => (
//                                <div key={k} className="cj-detail-row">
//                                    <span className="cj-detail-row__key">
//                                        {k.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}
//                                    </span>
//                                    <span className="cj-detail-row__val">{String(v)}</span>
//                                </div>
//                            ))
//                        }
//                    </div>
//                )}
                }
            </div>
        </div>
    );
};

// loader

const ContactDetailsLoader = () => {
    return (
        <div className="contact-page">
            {/* Header */}
            <div className="contact-header">
                <div className="contact-header__left">
                    <Skeleton
                        variant="circular"
                        width={72}
                        height={72}
                    />

                    <div style={{ flex: 1 }}>
                        <Skeleton width={220} height={32} />
                        <Skeleton width={160} height={20} />
                        <Skeleton width={280} height={20} />
                    </div>
                </div>
            </div>

            {/* Contact Details */}
            <div className="section">
                <Skeleton width={180} height={30} />

                <div className="grid">
                    {[...Array(5)].map((_, index) => (
                        <div key={index} className="item">
                            <Skeleton width={80} height={18} />
                            <Skeleton width="100%" height={28} />
                        </div>
                    ))}
                </div>
            </div>

            {/* Leads */}
            <div className="section">
                <Skeleton width={120} height={30} />

                <div className="leads-table-wrapper">
                    <table className="leads-table">
                        <thead>
                            <tr>
                                <th><Skeleton width={20} /></th>
                                <th><Skeleton width={80} /></th>
                                <th><Skeleton width={80} /></th>
                                <th><Skeleton width={80} /></th>
                                <th><Skeleton width={120} /></th>
                            </tr>
                        </thead>

                        <tbody>
                            {[...Array(5)].map((_, i) => (
                                <tr key={i}>
                                    <td>
                                        <Skeleton
                                            variant="circular"
                                            width={12}
                                            height={12}
                                        />
                                    </td>
                                    <td><Skeleton width={140} /></td>
                                    <td><Skeleton width={110} /></td>
                                    <td><Skeleton width={90} /></td>
                                    <td><Skeleton width="100%" /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

// ─── Main ─────────────────────────────────────────────────────────────────────

const ContactDetailsPage = (props) => {

    const paramNameCardId = useParamHook("nameCardId");
    const nameCardId = props?.nameCardId || paramNameCardId;
    const campaignJobId = useParamHook('campaignJobId');
    const navigate      = useNavigate();

    const [cursor, setCursor] = useState('auto');
    const [labelList,   setLabelList]   = useState([]);
    const [campaignJob, setCampaignJob] = useState(null);
    const [loading,     setLoading]     = useState(true);
    const [contact,     setContact]     = useState(null);
    const [leads,       setLeads]       = useState([]);
    const [page, setPage]               = useState(1);
    const [size]                        = useState(10);
    const [editing,     setEditing]     = useState(false);
    const [saving,      setSaving]      = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [totalLeads, setTotalLeads]   = useState(0);
    const [loadingMore, setLoadingMore] = useState(false);
    const [showDeleteForm, setShowDeleteForm] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [ref, inView] = useInView({
        triggerOnce: false,
        rootMargin: '50px'
    });

    const { currentProjectId } = useReduxProject();
    const { activeUserId } = useReduxActiveUser();
    const { currentPhoneNumber } = useReduxLeads();

    const defaultFilter = {
        leadsFilter: { projectIdList: [currentProjectId], statusList: [], sortBy: 'createTimeStamp', sortAsc: false },
        page: 1, size: 10,
    };

    const registryLinkViewService = new RegistryLinkViewService();
    const campaignJobService      = new CampaignJobService();
    const nameCardService         = new NameCardService();
    const leadsService            = new LeadsService();
    const errorHandler            = new ErrorHandler();

    useEffect(() => { renderData(); }, []);

    useEffect(() => {
        if (inView && hasMore && !loading && !loadingMore) {
            fetchLeadsData(page, contact);
        }
    }, [inView]);

    const renderData = async () => {
        setLoading(true);
        if (nameCardId)    await fetchNameCard();
        if (campaignJobId) await fetchCampaignJob();
        setLoading(false);
    };

    const fetchNameCard = async () => {
        try {
            const res = await nameCardService.getById(nameCardId);
            if (res.error) return;
            const data = JSON.parse(res.body);
            setContact(data);
            fetchLabelsForContacts();
            fetchLeadsData(page - 1, data);
        } catch (err) { errorHandler.handleError(err); }
    };

    const fetchLabelsForContacts = async () => {
        try {
            const res = await registryLinkViewService.getListByTargetTypeAndTargetId('nameCard', nameCardId);
            if (res.status < 400) setLabelList(JSON.parse(res.body));
        } catch (err) { errorHandler.handleError(err); }
    };

    const fetchCampaignJob = async () => {
        try {
            const res = await campaignJobService.getById(campaignJobId);
            if (res.status < 400) {
                const data = JSON.parse(res.body);
                setCampaignJob({ ...data, details: safeJsonParse(data.details, {}) });
            }
        } catch (err) { errorHandler.handleError(err); }
    };

    const fetchLeadsData = async (apiPage, nameCard) => {
        if (loadingMore) return;
        setLoadingMore(true);
        try {
            const filter = { ...defaultFilter, page: Math.max(apiPage, 0), size };
            filter.leadsFilter.contactNumber = nameCard?.contactNumber;
            const res = await leadsService.getFilteredList(filter);
            if (res.status < 400) {
                const body = JSON.parse(res.body);
                const newLeads = body.leadsList.map(item => ({
                    ...item,
                    details: safeJsonParse(item.details, {}),
                    history: safeJsonParse(item.history, []),
                    comments: safeJsonParse(item.comments, []),
                }));
                setLeads(prev => apiPage === 0 ? newLeads : [...prev, ...newLeads]);
                const nextPage = apiPage + 1;
                setPage(nextPage);
                setHasMore(
                    nextPage < body.totalPages
                );
                setTotalLeads(body.totalItems);
            }
        } catch (err) { errorHandler.handleError(err); }
        setLoadingMore(false);
    };

    const handleSaveContact = async (formData) => {
        setSaving(true);
        try {
            let bodyObj = {
                nameCard: {
                    ...contact,
                    ...formData,
                    id: nameCardId
                }
            };
            let res = await nameCardService.updateNameCard(bodyObj);
            if (res.status < 400) {
                res = JSON.parse(res.body);
                setContact(res);
                setEditing(false);
                SnackbarManager.show('Contact updated successfully', 'success');
            } else {
                SnackbarManager.show('Failed to update. Please try again.', 'error');
            }
        } catch (err) {
            errorHandler.handleError(err);
        }
        setSaving(false);
    };

    const generateAiSummery = async()=> {
        if(!!contact.query && contact?.query !== "") {
            setShowCreateModal(true);
            return;
        }
        setCursor("wait");
        try {
            let target = contact?.areaCode + contact?.contactNumber;
            let bodyObj = {
                target: target,
                phoneNumberId: currentPhoneNumber.id,
                projectId: currentProjectId,
            }
            let response = await leadsService.generateAiSummery(bodyObj);
            if(response.status < 400) {
                response = JSON.parse(response.body);
                setContact((prev)=> ({...prev, query: response}));
                setShowCreateModal(true);
            }
        } catch (err) {
            errorHandler.handleError(err);
        }
        setCursor("auto");
    }

    const onClose = ()=> !!props?.onClose ? props?.onClose() : navigate(-1);

    const handleDeleteNameCard = async ()=> {
        setCursor('wait');
        try {
            let body = {
                id: nameCardId
            };
            let response = await nameCardService.deleteNameCard(body);
            if(response.status < 400) {
                response = JSON.parse(response.body);
                await props?.reloadData();
                onClose();
            }
        } catch(err) {
            errorHandler.handleError(err);
        }
        setCursor('auto');
    }

    const toggleDeleteForm = ()=> {
        setShowDeleteForm(!showDeleteForm)
    }

    const initials = contact?.name
        ? contact.name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()
        : '?';

    if (loading) {
        return (
            <Drawer
                anchor="right"
                open={true}
                onClose={() => navigate(-1)}
            >
                <ContactDetailsLoader />
            </Drawer>
        );
    }

    return (
        <Drawer
            size={'lg'}
            anchor='right'
            open={true}
            onClose={onClose}>
            <div className="contact-page" style={{cursor: cursor}}>
                <div className="contact-page__topbar">
                    <Button startIcon={<ArrowLeft size={15} />} onClick={()=> onClose()}>
                        Back
                    </Button>
                    <div className='flex-row-center-0p5rem-gap'>
                        <Button startIcon={<User size={15} />} onClick={generateAiSummery} disabled={cursor === 'wait'}>
                            Convert to Lead
                        </Button>
                        <Button
                            startIcon={<Trash2 size={15} />}
                            className="contact-page__topbar__delete"
                            onClick={toggleDeleteForm} disabled={cursor === 'wait'}>
                                Delete
                        </Button>
                    </div>
                </div>

                {/* ── Contact header ── */}
                <div className="contact-header">
                    <div className="contact-header__left">
                        <div className="avatar">{initials}</div>
                        <div className="meta">
                            <div className="name">{contact?.name}</div>
                            {(contact?.designation || contact?.department) && (
                                <div className="sub">
                                    {[contact.designation, contact.department].filter(Boolean).join(' • ')}
                                </div>
                            )}
                            <div className="contact-line">
                                <span>+{contact?.areaCode} {contact?.contactNumber}</span>
                                {contact?.email && (
                                    <><span className="contact-line__sep">•</span>
                                    <a href={`mailto:${contact.email}`}>{contact.email}</a></>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Labels + unsubscribe anchored to header, right side */}
                    <div className="contact-header__right">
                        {labelList.length > 0 && (
                            <div className="contact-header__labels">
                                <Tag size={12} className="contact-header__labels-icon" />
                                {labelList.map(lbl => (
                                    <LabelClickUpMenu key={lbl.id} label={lbl} />
                                ))}
                            </div>
                        )}
                        <UserUnsubscribe id={nameCardId} />
                    </div>
                </div>

                {/* ── Contact Details ── */}
                <div className="section">
                    <div className="section-title section-title--with-action">
                        <span>Contact Details</span>
                        {!editing && (
                            <button className="section-edit-btn" onClick={() => setEditing(true)}>
                                <Pencil size={13} /> Edit
                            </button>
                        )}
                    </div>
                    {editing ? (
                        <ContactEditForm
                            contact={contact}
                            onSave={handleSaveContact}
                            onCancel={() => setEditing(false)}
                            saving={saving}
                        />
                    ) : (
                        <div className="grid">
                            <Item label="Organization" value={contact?.organizationName} />
                            <Item label="State"    value={contact?.state} />
                            <Item label="Country"  value={contact?.country} />
                            <Item label="Pin Code" value={contact?.pinCode} />
                            <Item label="Remarks"  value={contact?.remarks} />
                        </div>
                    )}
                </div>

                {/* ── Campaign Job ── */}
                {campaignJobId && <CampaignJobSection campaignJob={campaignJob} />}

                {/* ── Leads ── */}
                <div className="section section-load_more">
                    <div className="section-title">
                        Leads
                        {totalLeads > 0 && <span className="count-badge">{totalLeads}</span>}
                    </div>
                    {leads.length === 0 ? (
                        <div className="timeline-placeholder">No leads yet</div>
                    ) : (
                        <div className="leads-table-wrapper">
                            <table className="leads-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: 32 }} />
                                        <th>Name</th>
                                        <th>Phone</th>
                                        <th>Status</th>
                                        <th>Query</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {leads.map(lead => <LeadRow key={lead.id} lead={lead} />)}
                                </tbody>
                            </table>
                        </div>
                    )}
                    {
                        hasMore && (
                            <div ref={ref} className={'contactDetailsPage' + '__loadMore'}>
                                {
                                    loadingMore && (
                                        <>
                                            <CircularProgress size={24} />
                                            <span>
                                                Loading more...
                                            </span>
                                        </>
                                    )
                                }
                            </div>
                        )
                    }
                </div>
            </div>
            {showCreateModal && (
                <CreateLeadsForm contact={{...contact, source: 'whatsapp'}} onClose={() => setShowCreateModal(false)}/>
            )}
            {
                showDeleteForm &&
                    <DeleteNameCardForm
                        open={showDeleteForm}
                        closePopup={toggleDeleteForm}
                        item={contact}
                        deleteNameCard={handleDeleteNameCard}
                    />
            }
        </Drawer>
    );
};

export default ContactDetailsPage;